import dayjs from "dayjs";

/**
 * Parser for the BSale "DTE de Compra" export (Documentos → Recepcionados → Exportar a Excel).
 *
 * IMPORTANT: the file BSale calls `.xls` is actually an **HTML table** with a `.xls` extension,
 * not a real spreadsheet. It MUST be parsed as HTML (read the `<td>` text). Reading it with a
 * spreadsheet library (SheetJS) mis-coerces the HTML numbers into garbage floats (e.g. an IVA of
 * `1.011.009` becomes `39823.999…`). Parsed as HTML the data is clean and reconciles to the peso.
 *
 * Layout: header/filter block, then 4 sections (Recepcionados / Reclamados / Aceptados / Sin
 * Considerar), each with a column-header row, data rows, and a Total row. Data columns:
 *   0 Fecha Emisión · 1 Tipo · 2 Folio · 3 RUT · 4 Razón Social · 5 Exento · 6 Neto · 7 IVA ·
 *   8 Impto. Específico · 9 Monto Total · …
 */

export type BsaleTipo = "factura_afecta" | "factura_exenta" | "nota_credito";
export type BsaleEstado = "recepcionado" | "aceptado";
export type BsaleExcludeReason = "din" | "pre_cutoff" | "tipo_desconocido";

const TIPO_MAP: Record<string, { tipo: BsaleTipo; code: string } | "din"> = {
  "Factura Electrónica": { tipo: "factura_afecta", code: "FE" },
  "Factura No Afecta o Exenta Electrónica": { tipo: "factura_exenta", code: "FNE" },
  "Nota de Crédito Electrónica": { tipo: "nota_credito", code: "NC" },
  "Declaración de Ingreso (DIN)": "din",
};

const SECTIONS = ["Recepcionados", "Reclamados", "Aceptados", "Sin Considerar"];
const PAYABLE_SECTIONS = new Set(["Recepcionados", "Aceptados"]);

export interface ParsedBsaleInvoice {
  rut: string;
  razon_social: string;
  document_type: string;
  doc_tipo_code: string;
  tipo: BsaleTipo;
  folio: string;
  emission_date: string; // ISO yyyy-mm-dd
  bsale_estado: BsaleEstado;
  exento: number;
  neto: number;
  iva: number;
  impto_especifico: number;
  total: number; // signed — notas de crédito are negative
}

export interface ParsedBsaleExcluded {
  rut: string;
  razon_social: string;
  document_type: string;
  folio: string;
  emission_date: string | null;
  total: number;
  reason: BsaleExcludeReason;
}

export interface BsaleSectionRecon {
  section: string;
  parsed: number;
  reported: number;
  ok: boolean;
}

export interface BsaleParseResult {
  invoices: ParsedBsaleInvoice[]; // payable, tipo conocido, emisión ≥ cutoff
  excluded: ParsedBsaleExcluded[]; // DIN / pre-cutoff / tipo desconocido
  reconciliation: BsaleSectionRecon[];
  reconciles: boolean;
  warnings: string[];
}

const decodeEntities = (s: string): string =>
  s
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&#39;/g, "'")
    .replace(/&quot;/g, '"');

/** Extract the table as rows of trimmed cell text. */
function extractRows(html: string): string[][] {
  const rows: string[][] = [];
  const trRe = /<tr[^>]*>([\s\S]*?)<\/tr>/gi;
  let m: RegExpExecArray | null;
  while ((m = trRe.exec(html)) !== null) {
    const cells = [...m[1].matchAll(/<t[dh][^>]*>([\s\S]*?)<\/t[dh]>/gi)].map((c) =>
      decodeEntities(c[1].replace(/<[^>]+>/g, "")).trim()
    );
    rows.push(cells);
  }
  return rows;
}

/** Chilean-formatted integer amount → number (dots are thousands separators). */
function num(s: string | undefined): number {
  if (!s) return 0;
  const cleaned = s.replace(/\./g, "").replace(/[^0-9-]/g, "");
  return cleaned === "" || cleaned === "-" ? 0 : parseInt(cleaned, 10);
}

/** DD/MM/YYYY or D/M/YY (Chilean, day-first) → ISO yyyy-mm-dd. */
function toIso(s: string | undefined): string | null {
  if (!s) return null;
  const m = s.trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/);
  if (!m) return null;
  const [, d, mo, yRaw] = m;
  const y = yRaw.length === 2 ? `20${yRaw}` : yRaw;
  const iso = `${y}-${mo.padStart(2, "0")}-${d.padStart(2, "0")}`;
  return dayjs(iso).isValid() ? iso : null;
}

const normalizeRut = (s: string): string =>
  s.replace(/\./g, "").replace(/\s/g, "").toUpperCase();

export function parseBsaleDteCompra(
  html: string,
  opts: { cutoff: string }
): BsaleParseResult {
  const rows = extractRows(html);
  const invoices: ParsedBsaleInvoice[] = [];
  const excluded: ParsedBsaleExcluded[] = [];
  const parsedTotals: Record<string, number> = {};
  const reportedTotals: Record<string, number> = {};
  const warnings: string[] = [];
  let cur: string | null = null;

  for (const r of rows) {
    const c0 = (r[0] || "").trim();
    if (r.length === 1 && SECTIONS.includes(c0)) {
      cur = c0;
      parsedTotals[cur] = 0;
      continue;
    }
    if (!cur) continue;
    if ((r[1] || "").trim() === "Total") {
      reportedTotals[cur] = num(r[r.length - 1]); // Total row: total is the last cell
      continue;
    }
    if (c0.startsWith("Fecha Emis")) continue; // column-header row

    const folio = (r[2] || "").trim();
    const rutRaw = (r[3] || "").trim();
    const emision = toIso(r[0]);
    if (!(folio && /\d/.test(rutRaw) && emision)) continue; // not a data row

    const total = num(r[9]);
    parsedTotals[cur] += total; // section total spans every tipo (incl. DIN + NC)
    if (!PAYABLE_SECTIONS.has(cur)) continue; // Reclamados / Sin Considerar are not loaded

    const tipoRaw = (r[1] || "").trim();
    const mapped = TIPO_MAP[tipoRaw];
    const base = {
      rut: normalizeRut(rutRaw),
      razon_social: (r[4] || "").trim(),
      document_type: tipoRaw,
      folio,
      emission_date: emision,
      total,
    };

    if (mapped === "din") {
      excluded.push({ ...base, reason: "din" });
      continue;
    }
    if (!mapped) {
      excluded.push({ ...base, reason: "tipo_desconocido" });
      warnings.push(`Tipo de documento desconocido: "${tipoRaw}" (folio ${folio})`);
      continue;
    }
    if (emision < opts.cutoff) {
      excluded.push({ ...base, reason: "pre_cutoff" });
      continue;
    }

    invoices.push({
      ...base,
      doc_tipo_code: mapped.code,
      tipo: mapped.tipo,
      bsale_estado: cur === "Aceptados" ? "aceptado" : "recepcionado",
      exento: num(r[5]),
      neto: num(r[6]),
      iva: num(r[7]),
      impto_especifico: num(r[8]),
    });
  }

  const reconciliation: BsaleSectionRecon[] = SECTIONS.filter(
    (s) => reportedTotals[s] != null
  ).map((s) => ({
    section: s,
    parsed: parsedTotals[s] || 0,
    reported: reportedTotals[s] || 0,
    ok: Math.abs((parsedTotals[s] || 0) - (reportedTotals[s] || 0)) < 1,
  }));

  return {
    invoices,
    excluded,
    reconciliation,
    reconciles: reconciliation.every((x) => x.ok),
    warnings,
  };
}
